import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { AppState } from '../../state/reducers';
import * as MarketingActions from '../../state/marketing/marketing.actions';
import * as MarketingSelectors from '../../state/marketing/marketing.selectors';
import { MarketingReportResponse } from '../../services/marketing.service';
import { AuthService } from "../../services/auth.service";
import { PopupService } from '../../services/popup.service';

@Component({
  selector: 'app-market-insight',
  templateUrl: './market-insight.component.html',
  styleUrls: ['./market-insight.component.scss']
})
export class MarketInsightComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Observables from store
  marketingReport$: Observable<MarketingReportResponse | null>;
  loading$: Observable<boolean>;
  error$: Observable<any>;

  // Observable properties for template
  creationDate$: Observable<string>;
  creator$: Observable<string>;
  client$: Observable<string>;
  clientWebsite$: Observable<string>;
  scope$: Observable<string>;
  welcomeMessage$: Observable<string>;

  whyReadDescription$: Observable<string>;
  companiesAnalyzed$: Observable<string>;
  dataApproachDescription$: Observable<string>;
  nextStepsDescription$: Observable<string>;

  clientDescription$: Observable<string>;
  clientDetails$: Observable<string>;

  businessTags$: Observable<string[]>;
  mainBusinessCategory$: Observable<string>;
  mainIndustry$: Observable<string>;
  mainSector$: Observable<string>;

  websiteUrl$: Observable<string>;
  facebookUrl$: Observable<string>;
  linkedinUrl$: Observable<string>;
  instagramUrl$: Observable<string>;

  topDescriptionKeywords$: Observable<string[]>;
  topBusinessTagsTrend$: Observable<string[]>;
  techMentions$: Observable<string[]>;

  client_name$: Observable<string>;
  agency_service$: Observable<string>;

  // Additional observables for geographic dispersion and industry snapshot
  totalCompetitorsLocation$: Observable<number>;
  totalCompetitorsContinent$: Observable<number>;
  topCities$: Observable<Array<{city: string, percentage: number}>>;
  countryBreakdown$: Observable<Array<{country: string, percentage: number}>>;
  averageYearFounded$: Observable<number>;
  companyAgeDistribution$: Observable<Record<string, number>>;

  user: any;

  // Default values (fallbacks when no API data)
  private defaults = {
    creationDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    creator: 'Marketing Team',
    scope: 'Help you grow with #NoBS B2B digital marketing services.',
    welcomeMessage: 'Welcome to your personalized market-insight proposal.',
    whyReadDescription: 'This comprehensive market insight proposal has been meticulously crafted to provide you with actionable intelligence about your industry, competitors, and market positioning.',
    dataApproachDescription: 'Our approach is data-driven, replacing gut feelings with concrete insights derived from extensive market research.',
    nextStepsDescription: 'Next, We\'ll break down your Ideal Customer Profile on LinkedIn Sales Navigator once we kick off the project.',
    businessTags: ['SaaS', 'Technology', 'B2B'],
    mainBusinessCategory: 'Technology',
    mainIndustry: 'Software',
    mainSector: 'Information Technology',
    topDescriptionKeywords: ['innovation', 'growth', 'technology'],
    topBusinessTagsTrend: ['SaaS', 'automation', 'integration'],
    techMentions: ['cloud', 'AI', 'automation'],
    agency_service: 'B2B growth strategy'
  };

  constructor(
    private store: Store<AppState>, 
    private authService: AuthService,
    private popupService: PopupService
  ) {

    this.authService.authUser().subscribe((user) => {
      this.user = user;
    });

    // Initialize observables
    this.marketingReport$ = this.store.select(MarketingSelectors.selectMarketingReport);
    this.loading$ = this.store.select(MarketingSelectors.selectMarketingLoading);
    this.error$ = this.store.select(MarketingSelectors.selectMarketingError);

    // Map company data to template properties
    this.creationDate$ = this.marketingReport$.pipe(
      map(report => report?.data?.report_metadata?.creation_date || this.defaults.creationDate)
    );

    this.creator$ = this.marketingReport$.pipe(
      map(() => this.defaults.creator)
    );

    this.client$ = this.marketingReport$.pipe(
      map(report => report?.data?.report_metadata?.client_name || 'Your Company')
    );

    this.clientWebsite$ = this.marketingReport$.pipe(
      map(report => {
        const url = report?.data?.report_metadata?.client_website || 'https://yourcompany.com';
        return url.replace('https://', '').replace('http://', '');
      })
    );

    this.scope$ = this.marketingReport$.pipe(
      map(() => this.defaults.scope)
    );

    this.welcomeMessage$ = this.marketingReport$.pipe(
      map(report => {
        const companyName = report?.data?.report_metadata?.client_name || 'your company';
        return `Welcome to your personalized market-insight proposal. This one-pager is designed to give ${companyName}'s leadership a clear, data-grounded snapshot of your competitive landscape, your own company profile, and the next steps we recommend, all in a single, easy-to-digest document.`;
      })
    );

    this.whyReadDescription$ = this.marketingReport$.pipe(
      map(() => this.defaults.whyReadDescription)
    );

    this.companiesAnalyzed$ = this.marketingReport$.pipe(
      map(report => (report?.data?.industry_snapshot?.total_competitors_in_continent || 0).toString())
    );

    this.dataApproachDescription$ = this.marketingReport$.pipe(
      map(() => this.defaults.dataApproachDescription)
    );

    this.nextStepsDescription$ = this.marketingReport$.pipe(
      map(() => this.defaults.nextStepsDescription)
    );

    this.clientDescription$ = this.marketingReport$.pipe(
      map(report => report?.data?.client_descriptors?.short_description || `Your company provides innovative solutions in the technology sector.`)
    );

    this.clientDetails$ = this.marketingReport$.pipe(
      map(report => report?.data?.client_descriptors?.long_description || 'Company details will be displayed here once the report is generated.')
    );

    this.businessTags$ = this.marketingReport$.pipe(
      map(report => report?.data?.business_tags?.tags || this.defaults.businessTags)
    );

    this.mainBusinessCategory$ = this.marketingReport$.pipe(
      map(report => report?.data?.business_tags?.main_business_category || this.defaults.mainBusinessCategory)
    );

    this.mainIndustry$ = this.marketingReport$.pipe(
      map(report => report?.data?.business_tags?.main_industry || this.defaults.mainIndustry)
    );

    this.mainSector$ = this.marketingReport$.pipe(
      map(report => report?.data?.business_tags?.main_sector || this.defaults.mainSector)
    );

    this.websiteUrl$ = this.marketingReport$.pipe(
      map(report => {
        const url = report?.data?.assets?.website || 'https://example.com';
        return url.replace('https://', '').replace('http://', '');
      })
    );

    this.facebookUrl$ = this.marketingReport$.pipe(
      map(report => {
        const url = report?.data?.assets?.facebook_url;
        if (url) {
          return url.replace('https://', '').replace('http://', '');
        }
        return 'facebook.com/yourcompany';
      })
    );

    this.linkedinUrl$ = this.marketingReport$.pipe(
      map(report => {
        const url = report?.data?.assets?.linkedin_url;
        if (url) {
          return url.replace('https://', '').replace('http://', '');
        }
        return 'linkedin.com/company/yourcompany';
      })
    );

    this.instagramUrl$ = this.marketingReport$.pipe(
      map(report => {
        const url = report?.data?.assets?.instagram_url;
        if (url) {
          return url.replace('https://', '').replace('http://', '');
        }
        return 'instagram.com/yourcompany';
      })
    );

    this.topDescriptionKeywords$ = this.marketingReport$.pipe(
      map(report => report?.data?.trend_analysis?.short_description_themes || this.defaults.topDescriptionKeywords)
    );

    this.topBusinessTagsTrend$ = this.marketingReport$.pipe(
      map(report => report?.data?.trend_analysis?.business_tags?.slice(0, 10) || this.defaults.topBusinessTagsTrend)
    );

    this.techMentions$ = this.marketingReport$.pipe(
      map(report => report?.data?.trend_analysis?.product_technology_mentions?.slice(0, 10) || this.defaults.techMentions)
    );

    this.client_name$ = this.marketingReport$.pipe(
      map(report => report?.data?.report_metadata?.client_name || 'Your Company')
    );

    this.agency_service$ = this.marketingReport$.pipe(
      map(() => this.defaults.agency_service)
    );

    // Initialize additional observables
    this.totalCompetitorsLocation$ = this.marketingReport$.pipe(
      map(report => report?.data?.industry_snapshot?.total_competitors_in_location || 0)
    );

    this.totalCompetitorsContinent$ = this.marketingReport$.pipe(
      map(report => report?.data?.industry_snapshot?.total_competitors_in_continent || 0)
    );

    this.topCities$ = this.marketingReport$.pipe(
      map(report => report?.data?.geographic_dispersion?.top_10_cities || [])
    );

    this.countryBreakdown$ = this.marketingReport$.pipe(
      map(report => report?.data?.geographic_dispersion?.country_breakdown || [])
    );

    this.averageYearFounded$ = this.marketingReport$.pipe(
      map(report => report?.data?.company_age?.average_year_founded || new Date().getFullYear())
    );

    this.companyAgeDistribution$ = this.marketingReport$.pipe(
      map(report => report?.data?.company_age?.distribution_highlights || {})
    );
  }

  ngOnInit(): void {
    const email = this.user.email;
    this.loadMarketingReport(email);

    // Subscribe to errors and show notifications
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        const errorMessage = this.extractErrorMessage(error);
        this.popupService.notify(errorMessage, 'Market Report Error', 'error');
      }
    });
  }

  extractErrorMessage(error: any): string {
    // Handle different error response formats
    if (typeof error === 'string') {
      return error;
    }
    
    // Check for API error response format
    if (error?.error?.message) {
      return error.error.message;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    // If error object has error property with string
    if (error?.error && typeof error.error === 'string') {
      return error.error;
    }
    
    // Default error message
    return 'Failed to generate market report. Please try again later.';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMarketingReport(email: string): void {
    if (!email || !this.validateEmail(email)) {
      console.error('Invalid email address');
      return;
    }
    this.store.dispatch(MarketingActions.loadMarketingReport({ email }));
  }


  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  receiveSidebarTabs(data: any) {
    console.log('Sidebar tabs received:', data);
  }

  receiveAside(event: any) {
    console.log('Aside event received:', event);
  }

  handleAsideStateChange(state: { isOpen: boolean; isClosing: boolean }) {
    console.log('Aside state change:', state);
  }
}
